// refresh notification handled via hook
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import {
  snakeToCamel,
  useBusinessService,
  useBusinessServiceRequest,
  useCurrentUser,
  useCurrentUserDateTimeFormat,
  useQueryFirstLoading,
} from '@ingenico/react-section-utils';
import type {
  DatagridQuickFilter,
  DataGridSimpleQuickFilter,
  DefaultQuickFilter,
  QuickFilterDataToRefetchParams,
} from '@ingenico/react-ui/advanced-search';
import {
  DatagridQuickFilterResetType,
  DatagridQuickFilterTypeEnum,
  useQuickFilters,
} from '@ingenico/react-ui/advanced-search';
import { SEARCH_DEBOUNCE_TIMEOUT } from '@ingenico/react-ui/consts';
import { DATAGRID_ROWS_PER_PAGE_OPTIONS } from '@ingenico/react-ui/datagrid';
import type { OnTriggerProps, UseDatagridQueryTriggerProps } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDatagridQueryTrigger } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDateRange } from '@ingenico/react-ui/hooks/use-date-range';
import { PeriodSelection } from '@ingenico/react-ui/models';
import type { DataGridMultiActionRef } from '@ingenico/react-ui/multi-actions';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import type { GridApiPremium, GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useDebouncedCallback } from '@react-hookz/web';
import { cloneDeep, isEmpty, isEqual, toArray, toString } from 'lodash-es';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import type { CoreError } from '../../domain/api/mappers/coreErrorConstructor';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import type {
  DeploymentUnitResourceItemV2,
  DeploymentUnitResourceMinimalV2,
} from '../../domain/models/deployment-unit.model';
import { enumDeploymentUnitStatus } from '../../domain/models/deployment-unit.model';
import type {
  SearchDeploymentUnitsBody,
  SearchDeploymentUnitsQueryParams,
} from '../../domain/models/deployment/deploymentUnitList.model';
import { useDeviceManagementError } from '../../hooks';
import { useRefreshDatagrid } from '../../hooks/useRefreshDatagrid.hook';
import { getCategoryQuickFilterQuery } from '../deployment-list/util/deploymentFieldValues.util';
import type {
  UseDeploymentTargetDevicesTabInput,
  UseDeploymentTargetDevicesTabOutput,
} from './DeploymentTargetDevicesTab.model';
import {
  defaultDeploymentTargetDevicesSortModel,
  getInitialColumnVisibilityModel,
  transformFiltersToRequestCall,
  transformSortModelForRequest,
} from './DeploymentTargetDevicesTab.util';
import { useDeploymentTargetDevicesTabColumns } from './useDeploymentTargetDevicesTabColumns.hook';
import { useDeploymentVirtualDevicesTabColumns } from './useDeploymentVirtualDevicesTabColumns.hook';

export function useDeploymentTargetDevicesTab({
  logicalDeviceId,
  isOpenedFromLogicalDevice,
  isExposedComponent = false,
}: UseDeploymentTargetDevicesTabInput): UseDeploymentTargetDevicesTabOutput {
  const { id: urlDeploymentId } = useParams<{ id: string }>();
  const deploymentId = useMemo(() => {
    if (isExposedComponent) {
      return undefined;
    }
    return urlDeploymentId || undefined;
  }, [urlDeploymentId, isExposedComponent]);

  const { t } = useTranslation([ALL_LNGS_NAMESPACES.DEPLOYMENT, ALL_LNGS_NAMESPACES.COMMON]);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const selectedTab = searchParams.get('selectedTab');
  const dataGridMultiActionsRef = useRef<DataGridMultiActionRef>(null);
  const datagridRef = useGridApiRef();

  const { locale: userLocale, zoneInfo } = useCurrentUser();

  const {
    startDate,
    endDate,
    getDateRangeStatus,
    setDateRange,
    isError: isDateRangeError,
    setPeriod,
    timePeriod,
    isStartDateError,
    isEndDateError,
  } = useDateRange({ zoneInfo, customPeriod: PeriodSelection.Last30Days });

  const [searchQuery] = useState<string>('');
  const [selectedQuickFilterValue, setSelectedQuickFilterValue] = useState<string>();
  const [selectedConfigProfileId, setSelectedConfigProfileId] = useState<string>();
  const [searchRequestQuery, setSearchRequestQuery] = useState<string>();
  const [limit, setLimit] = useState(DATAGRID_ROWS_PER_PAGE_OPTIONS[0]);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<GridSortModel>(defaultDeploymentTargetDevicesSortModel);
  const [isConfigurationQuickFilterRefetch, setIsConfigurationQuickFilterRefetch] = useState<boolean>(false);
  const [quickFiltersErrors, setQuickFiltersErrors] = useState<Record<string, string | undefined>>({});

  const requestBody = useMemo<SearchDeploymentUnitsBody>(() => {
    return {
      filters: transformFiltersToRequestCall({
        selectedQuickFilterStatusValue: selectedQuickFilterValue,
        searchQuery: searchRequestQuery,
        startDate,
        endDate,
        deploymentId,
        logicalDeviceId,
        isOpenedFromLogicalDevice,
        selectedConfigProfileId,
      }),
      sort: transformSortModelForRequest(sort),
    };
  }, [
    deploymentId,
    endDate,
    isOpenedFromLogicalDevice,
    logicalDeviceId,
    searchRequestQuery,
    selectedQuickFilterValue,
    sort,
    startDate,
    selectedConfigProfileId,
  ]);

  const requestParams = useMemo<SearchDeploymentUnitsQueryParams>(() => {
    return { offset, limit };
  }, [offset, limit]);

  const query = useMemo(
    () => ({
      body: requestBody,
      params: requestParams,
    }),
    [requestBody, requestParams],
  );

  /* apis */
  const searchDeploymentUnitsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) => deviceManagementService.searchDeploymentUnits(query, isRefetch),
      [query],
    ),
  );
  const { getErrorMessage } = useDeviceManagementError();

  const datagridErrorMessage = useMemo(() => {
    if (searchDeploymentUnitsQuery.isError) {
      return getErrorMessage(searchDeploymentUnitsQuery.error, t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }));
    }
    return '';
  }, [getErrorMessage, searchDeploymentUnitsQuery.error, searchDeploymentUnitsQuery.isError, t]);
  /* apis */

  /* searchbar */
  const setDebouncedSearchQuery = useDebouncedCallback(
    (text: string) => setSearchRequestQuery(text),
    [],

    SEARCH_DEBOUNCE_TIMEOUT,
  );

  const handleSearchQueryChange = useCallback(
    (searchValue: string) => setDebouncedSearchQuery(searchValue),
    [setDebouncedSearchQuery],
  );

  const advancedSearch = useMemo(
    () => ({
      searchQuery: toString(searchQuery),
      handleSearchQueryChange,
    }),
    [searchQuery, handleSearchQueryChange],
  );
  /* searchbar */

  /* refresh button */
  const { handleRefresh } = useRefreshDatagrid(searchDeploymentUnitsQuery);
  /* refresh button */

  /* quickfilter status */
  const defaultQuickFilters = useCallback(() => {
    const configurationProfileFilter = isOpenedFromLogicalDevice
      ? [
          {
            order: 0,
            field: 'deploymentRequest.configuration.id',
            label: t('field.configurationProfile', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
            searchMode: 'server',
            options: [],
          },
        ]
      : [];

    return [
      ...configurationProfileFilter,
      {
        order: 1,
        field: 'status',
        label: t('field.targetDevices.status', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        options: [],
        searchMode: 'server',
      },
    ] as DefaultQuickFilter[];
  }, [isOpenedFromLogicalDevice, t]);
  const getLabelQuickFilterLabel = useCallback((name: string) => name, []);

  const { filterRefApi, isLoadingQuickFilters, fields } = useQuickFilters({
    defaultQuickFilters: defaultQuickFilters(),
    getLabel: getLabelQuickFilterLabel,
  });

  const currentFiltersIndentifier = useMemo(
    () => defaultQuickFilters().map(({ field }) => field),
    [defaultQuickFilters],
  );

  const checkAllowReset = useCallback(
    (fieldName?: string) => !currentFiltersIndentifier.includes(toString(fieldName)),
    [currentFiltersIndentifier],
  );

  const overrideDefaultFilterTypes = useCallback(
    (field: string, currentType: DatagridQuickFilterTypeEnum) => {
      return currentFiltersIndentifier.includes(field) ? DatagridQuickFilterTypeEnum.SingleSelect : currentType;
    },
    [currentFiltersIndentifier],
  );

  const quickFilterCall = useCallback(async () => {
    const statuses = Object.values(enumDeploymentUnitStatus).filter(
      (status) => status !== enumDeploymentUnitStatus.PAUSED,
    );
    return Promise.resolve(
      statuses.map((status) => {
        const statusLabel = toString(snakeToCamel(status));
        return {
          label: t(`status.${statusLabel}`, { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
          value: status ?? 'failed',
        };
      }) ?? [],
    );
  }, [t]);

  const handleFilterChange = useCallback((quickFilters: DatagridQuickFilter[]) => {
    if (isEmpty(quickFilters)) {
      setSelectedQuickFilterValue(undefined);
    } else {
      const statusFilter = (quickFilters as DataGridSimpleQuickFilter[]).find(
        (quickFilter) => quickFilter.input.field === 'status',
      );
      if (statusFilter) {
        const { input } = statusFilter;
        const { criteria } = input;
        setSelectedQuickFilterValue((criteria as string[])[0]);
      }
      const configProfileFilter = (quickFilters as DataGridSimpleQuickFilter[]).find(
        (quickFilter) => quickFilter.input.field === 'deploymentRequest.configuration.id',
      );
      if (configProfileFilter) {
        const { input } = configProfileFilter;
        const { criteria } = input;
        setSelectedConfigProfileId((criteria as string[])[0]);
      }
    }
  }, []);
  /* quickfilter status */

  const userDateTimeFormat = useCurrentUserDateTimeFormat();

  /* quickfilter date range */
  const dateFilter = useMemo(
    () => ({
      startDate,
      endDate,
      getDateRangeStatus,
      setDateRange,
      isError: isDateRangeError,
      setPeriod,
      timePeriod,
      isStartDateError,
      isEndDateError,
      userDateTimeFormat,
    }),
    [
      endDate,
      getDateRangeStatus,
      isDateRangeError,
      isEndDateError,
      isStartDateError,
      setDateRange,
      setPeriod,
      startDate,
      timePeriod,
      userDateTimeFormat,
    ],
  );
  /* quickfilter date range */

  /* Datagrid */
  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-services',
      isBordered: false,
      messageLabel: 'emptyListOrPage.message',
      namespace: ALL_LNGS_NAMESPACES.COMMON,
      titleLabel: 'emptyListOrPage.title',
    }),
    [],
  );

  const { columns: columnsVirtualDevices, defaultAvailableColumns: virtualDevicesDefaultAvailableColumns } =
    useDeploymentVirtualDevicesTabColumns();
  const { columns: columnsTargetDevices, defaultAvailableColumns: targetDevicesDefaultAvailableColumns } =
    useDeploymentTargetDevicesTabColumns();

  // Use columnsTargetDevices when component is exposed in another section, otherwise use default columns
  const selectedColumns = useMemo(() => {
    return isExposedComponent ? columnsVirtualDevices : columnsTargetDevices;
  }, [isExposedComponent, columnsVirtualDevices, columnsTargetDevices]);

  const availableColumns = useMemo(
    () => (isExposedComponent ? virtualDevicesDefaultAvailableColumns : targetDevicesDefaultAvailableColumns),
    [isExposedComponent, targetDevicesDefaultAvailableColumns, virtualDevicesDefaultAvailableColumns],
  );

  const gridData = useMemo(
    () => ({
      data: toArray(searchDeploymentUnitsQuery.data?.data),
      isLoading: searchDeploymentUnitsQuery.isLoading,
      error: searchDeploymentUnitsQuery.error,
      isError: searchDeploymentUnitsQuery.isError,
      isSuccess: searchDeploymentUnitsQuery.isSuccess,
    }),
    [
      searchDeploymentUnitsQuery.data?.data,
      searchDeploymentUnitsQuery.error,
      searchDeploymentUnitsQuery.isError,
      searchDeploymentUnitsQuery.isLoading,
      searchDeploymentUnitsQuery.isSuccess,
    ],
  );

  const total = useMemo<number>(
    () => searchDeploymentUnitsQuery.data?.pagination.totalResults ?? 0,
    [searchDeploymentUnitsQuery.data?.pagination.totalResults],
  );

  const onTrigger = useCallback(({ page, pageSize, sort: newSort }: OnTriggerProps) => {
    setOffset(page * pageSize);
    setLimit(pageSize);
    setSort((current) =>
      isEmpty(newSort) && isEqual(cloneDeep(defaultDeploymentTargetDevicesSortModel), cloneDeep(current))
        ? current
        : newSort,
    );
  }, []);

  const serverData = useMemo<UseDatagridQueryTriggerProps['serverData']>(() => {
    return {
      offset,
      limit,
      total,
      sort,
    };
  }, [limit, offset, sort, total]);

  const datagridTriggers = useDatagridQueryTrigger({
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    onTrigger,
    serverData,
    filters: { searchQuery, selectedQuickFilterValue, startDate, endDate },
    setKeepNonExistentRowsSelected: dataGridMultiActionsRef?.current?.setKeepNonExistentRowsSelected,
  });

  const getRowId = useCallback((deploymentUnit: DeploymentUnitResourceItemV2) => toString(deploymentUnit.id), []);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    getInitialColumnVisibilityModel(availableColumns, { endDate: false }),
  );

  const handleChangeVisibleColumns = useCallback(
    (newGridColumnVisibilityModel: GridColumnVisibilityModel) => setColumnVisibilityModel(newGridColumnVisibilityModel),
    [],
  );

  const gridProps = useMemo(
    () => ({
      ref: datagridRef as MutableRefObject<GridApiPremium>,
      getRowId,
      total,
      handlePaginationModelChange: datagridTriggers.onPaginationModelChange,
      handleSortModelChange: datagridTriggers.onSortModelChange,
      columnVisibilityModel,
      handleChangeVisibleColumns,
      sortModel: sort,
    }),
    [
      columnVisibilityModel,
      datagridRef,
      datagridTriggers.onPaginationModelChange,
      datagridTriggers.onSortModelChange,
      getRowId,
      handleChangeVisibleColumns,
      total,
      sort,
    ],
  );
  /* Datagrid */

  const isFirstLoading = useQueryFirstLoading(searchDeploymentUnitsQuery) && !searchDeploymentUnitsQuery?.isError;

  const shouldDisplayGrid = useMemo<boolean>(
    () => (!!gridData.isSuccess || !!gridData.isLoading) && !isFirstLoading,
    [gridData, isFirstLoading],
  );

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<DeploymentUnitResourceMinimalV2>) => {
      const { id: targetDeviceId } = row;
      void navigate(`${targetDeviceId}${selectedTab ? `?selectedTab=${selectedTab}` : ''}`);
    },
    [navigate, selectedTab],
  );

  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);

  const handleIsConfigurationQuickFilterRefetch = useCallback(
    (isLoading: boolean) => setIsConfigurationQuickFilterRefetch(isLoading),
    [],
  );

  const manageQuickFilterError = useCallback(
    (key: string) => (error?: CoreError) =>
      setQuickFiltersErrors((currentError) => ({
        ...currentError,
        [key]:
          key === 'deploymentRequest.configuration.id' && error
            ? t('error.quickFilter.fetchConfigurationProfile', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT })
            : undefined,
      })),
    [t],
  );

  const handleConfigurationQuickFilterPromise = useCallback(
    async (params?: QuickFilterDataToRefetchParams) => {
      return getCategoryQuickFilterQuery(
        deviceManagementBusinessService,
        params,
        handleIsConfigurationQuickFilterRefetch,
        manageQuickFilterError('deploymentRequest.configuration.id'),
      );
    },
    [deviceManagementBusinessService, handleIsConfigurationQuickFilterRefetch, manageQuickFilterError],
  );

  const overrideResetFieldType = useMemo(
    () => ({
      status: DatagridQuickFilterResetType.FloatingButton,
      'deploymentRequest.configuration.id': DatagridQuickFilterResetType.FloatingButton,
    }),
    [],
  );

  const quickFiltersOptionListRequests = useMemo(
    () => ({
      status: quickFilterCall,
      'deploymentRequest.configuration.id': handleConfigurationQuickFilterPromise,
    }),
    [handleConfigurationQuickFilterPromise, quickFilterCall],
  );

  const isQuickFilterLoading = useMemo(
    () => ({
      'deploymentRequest.configuration.id': isLoadingQuickFilters || isConfigurationQuickFilterRefetch,
    }),
    [isConfigurationQuickFilterRefetch, isLoadingQuickFilters],
  );

  return {
    datagridErrorMessage,
    noRowsProps,
    columns: selectedColumns,
    response: gridData,
    gridProps,
    filterRefApi,
    isLoadingQuickFilters,
    fields,
    getLabelQuickFilterLabel,
    handleFilterChange,
    handleRowClick,
    overrideDefaultFilterTypes,
    quickFilterCall,
    checkAllowReset,
    shouldDisplayGrid,
    isFirstLoading,
    advancedSearch,
    dateFilter,
    userLocale,
    userDateTimeFormat,
    overrideResetFieldType,
    quickFiltersOptionListRequests,
    isQuickFilterLoading,
    handleRefresh,
    quickFiltersErrors,
  };
}
